import { Alert, Anchor, Stack, Text } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { ApiError } from "../lib/api.js";
import { describeApiError } from "../lib/errorText.js";

/**
 * Fehleranzeige für Abfragen — zentral, damit ein Berechtigungsfall überall
 * gleich benannt wird (Befund G2).
 *
 * Der Auslöser: Ein Projektleiter klickte in der BCG-Matrix auf einen Kunden,
 * für den er nicht zuständig ist, und bekam „Daten konnten nicht geladen
 * werden" — eine technische Meldung für einen Berechtigungsfall, ohne Weg
 * zurück. Deshalb hier:
 *
 * - **403** wird als fehlende Berechtigung benannt, nicht als Ladefehler.
 * - **404** heißt „nicht gefunden **oder** nicht freigegeben": Die Kunden-API
 *   antwortet auf einen nicht sichtbaren Kunden bewusst mit 404 statt 403,
 *   damit sie nicht verrät, welche Kunden es gibt (`customers.service.ts`).
 *   Die Meldung darf das nicht auseinanderreißen, was der Server bewusst
 *   zusammengelegt hat.
 * - Jeder Fall bietet einen Rückweg an, wenn einer übergeben wurde. Eine
 *   Detailseite ohne Rückweg ist eine Sackgasse.
 * - Alles andere bleibt der technische Ladefehler samt Code — dort ist die
 *   technische Einordnung hilfreich, weil sie gemeldet werden muss.
 */
export function ApiErrorAlert({
  error,
  backTo,
  backLabel,
}: {
  error: unknown;
  /** Ziel des Rückwegs, z. B. "/customers". Ohne Angabe kein Rückweg. */
  backTo?: string;
  /** Beschriftung des Rückwegs, z. B. „Kunden". */
  backLabel?: string;
}) {
  const { t } = useTranslation();

  const status = error instanceof ApiError ? error.status : null;
  const isForbidden = status === 403;
  const isNotFound = status === 404;
  const isPermissionCase = isForbidden || isNotFound;

  const title = isForbidden
    ? t("common.errorForbiddenTitle")
    : isNotFound
      ? t("common.errorNotFoundTitle")
      : t("common.error");

  const body = isForbidden
    ? t("common.errorForbiddenBody")
    : isNotFound
      ? t("common.errorNotFoundBody")
      : describeApiError(error, t);

  return (
    <Alert color={isPermissionCase ? "yellow" : "red"} title={title}>
      <Stack gap="xs" align="flex-start">
        <Text size="sm">{body}</Text>
        {backTo !== undefined && (
          <Anchor component={Link} to={backTo} size="sm">
            ← {backLabel ?? t("common.back")}
          </Anchor>
        )}
      </Stack>
    </Alert>
  );
}
